import React from 'react'
import { Box, Divider, Skeleton, Stack, Typography } from '@mui/material'
import {
  TCheckoutOrder,
  TCheckoutOrderDetails,
} from '../../../../../core/types/CheckoutOrder'
import { maskCpfCnpjNumber, maskPhoneNumber } from '../../../../../masks/masks'
import { formatarPhone } from '../../../../../core/functions/phoneUtils'
import ListItemDefault from '../../../../../components/ListItem/ListItemDefault'

export interface IOrderDetailCustomerProp {
  checkoutOrder?: TCheckoutOrder
  details?: TCheckoutOrderDetails
  loading: boolean
}

const EMPTY_VALUE = '—'

function formatZipCode(zip?: string | null) {
  if (!zip) return EMPTY_VALUE
  const onlyDigits = zip.replace(/\D/g, '')
  if (onlyDigits.length === 8) {
    return `${onlyDigits.slice(0, 5)}-${onlyDigits.slice(5)}`
  }
  return zip
}

function buildAddressLine(
  address?: TCheckoutOrderDetails['customerAddress'] | null
) {
  if (!address) return null

  const street = address.street?.trim()
  const number = address.number?.trim()
  const complement = address.complement?.trim()
  const neighborhood = address.neighborhood?.trim()
  const city = address.city?.trim()
  const state = address.state?.trim()

  const part1 = [street, number ? `nº ${number}` : null]
    .filter(Boolean)
    .join(', ')
  const part2 = [complement, neighborhood].filter(Boolean).join(' • ')
  const part3 = [city, state].filter(Boolean).join(' / ')

  const line = [part1, part2, part3].filter(Boolean).join(' — ')
  return line.length ? line : null
}

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
    {children}
  </Typography>
)

const OrderDetailCustomer = ({
  checkoutOrder,
  details,
  loading,
}: IOrderDetailCustomerProp) => {
  if (loading) {
    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Skeleton variant="rounded" width="100%" height="45px" />
        <Skeleton variant="rounded" width="100%" height="45px" />
        <Skeleton variant="rounded" width="100%" height="45px" />
        <Skeleton variant="rounded" width="100%" height="45px" />
      </Stack>
    )
  }

  const address = details?.customerAddress ?? null
  const addressLine = buildAddressLine(address)

  const phoneMasked = details?.customerPhone
    ? formatarPhone(
        details.customerPhone,
        details.customerPhoneCountryCode,
        details.customerCountryCode
      )
    : EMPTY_VALUE

  const documentMasked = details?.customerDocument
    ? maskCpfCnpjNumber(details.customerDocument)
    : EMPTY_VALUE

  const emailValue = checkoutOrder?.email?.trim() || EMPTY_VALUE
  const nameValue = checkoutOrder?.name?.trim() || EMPTY_VALUE

  const customFields = (details?.customFields ?? [])
    .filter((f) => !!f?.name)
    .map((f) => ({
      key: f.identity ?? `${f.name}-${String(f.value)}`,
      name: f.name,
      value: f.value ?? EMPTY_VALUE,
    }))

  return (
    <Box sx={{ p: 1 }}>
      <Stack spacing={1}>
        {/* DADOS DO CLIENTE */}
        <Box>
          <SectionTitle>Dados do cliente</SectionTitle>

          <ListItemDefault title="Nome" value={nameValue} />
          <ListItemDefault title="E-mail" value={emailValue} />
          <ListItemDefault title="Telefone" value={phoneMasked} />
          <ListItemDefault title="Documento" value={documentMasked} />
        </Box>

        <Divider />

        {/* ENDEREÇO */}
        <Box>
          <SectionTitle>Endereço</SectionTitle>

          <ListItemDefault
            title="Endereço completo"
            value={addressLine ?? 'Não informado'}
            viewDivider
          />

          {address && (
            <>
              <ListItemDefault
                title="Rua"
                value={address.street || EMPTY_VALUE}
              />
              <ListItemDefault
                title="Número"
                value={address.number || EMPTY_VALUE}
              />
              <ListItemDefault
                title="Complemento"
                value={address.complement || EMPTY_VALUE}
              />
              <ListItemDefault
                title="Bairro"
                value={address.neighborhood || EMPTY_VALUE}
              />
              <ListItemDefault
                title="Cidade"
                value={address.city || EMPTY_VALUE}
              />
              <ListItemDefault
                title="Estado"
                value={address.state || EMPTY_VALUE}
              />
              <ListItemDefault
                title="CEP"
                value={formatZipCode(address.zipCode)}
              />
            </>
          )}
        </Box>

        {/* CAMPOS PERSONALIZADOS */}
        <Divider />

        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <SectionTitle>Campos personalizados</SectionTitle>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {customFields.length} campo(s)
            </Typography>
          </Stack>

          {customFields.length === 0 ? (
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Nenhum campo personalizado foi informado nesta compra.
            </Typography>
          ) : (
            customFields.map((field) => (
              <ListItemDefault
                key={field.key}
                title={field.name}
                value={String(field.value)}
              />
            ))
          )}
        </Box>
      </Stack>
    </Box>
  )
}

export default OrderDetailCustomer
